<template>
  <div>
    <header class="modal-header bold pb25 weight-700 bg-cl-secondary">
      {{ $t('Log in') }}
      <i
        slot="close"
        class="modal-close icon icon-close-new fs24"
        @click="close"
      />
    </header>
    <div
      v-if="hasRedirect"
      class="pt10 pb10 redirect-error"
    >
      <p class="mb0 mt0">
        {{ $t('You need to be logged in to see this page') }}
      </p>
    </div>
    <div class="modal-content c-bg-white pt30 cl-secondary">
      <social-login />
      <form
        v-if="mode === MODE_CODE_REQUEST"
        novalidate
        @submit.prevent="sendLoginCode"
      >
        <base-input
          id="email"
          v-model="email"
          class="mb10"
          type="email"
          name="email"
          autocomplete="email"
          focus
          :placeholder="$t('E-mail address *')"
          :validations="[
            {
              condition: !$v.email.required && $v.email.$error,
              text: $t('Field is required.')
            },
            {
              condition: !$v.email.email && $v.email.$error,
              text: $t('Please provide valid e-mail address.')
            }
          ]"
        />
        <button-full
          class="mb20 button-primary"
          type="submit"
          data-testid="loginCodeRequest"
          :disabled="submitting"
        >
          {{ $t('Send login code') }}
        </button-full>
        <a
          href="#"
          class="row m0 mb20 fs14 lh20 underline"
          data-testid="loginPasswordSwitch"
          @click.prevent="mode = MODE_PASSWORD"
        >
          {{ $t('Log in with password') }}
        </a>
        <p class="m0 fs14 lh20 login-hint">
          {{ $t('Have you purchased from us before? Enter the same email address you used for your order and we will send you a login code.') }}
        </p>
      </form>
      <form
        v-else-if="mode === MODE_CODE_ENTRY"
        novalidate
        @submit.prevent="verifyLoginCode"
      >
        <base-input
          id="login-code"
          v-model="loginCode"
          class="mb10 ph-no-capture"
          type="text"
          name="login-code"
          autocomplete="one-time-code"
          :placeholder="$t('Login code')"
          :validations="[{
            condition: codeError !== '',
            text: codeError
          }]"
        />
        <button-full
          class="mb20 button-primary"
          type="submit"
          data-testid="loginCodeSubmit"
          :disabled="submitting"
        >
          {{ $t('Log in with code') }}
        </button-full>
        <a
          href="#"
          class="row m0 mb10 fs14 lh20 underline"
          :class="{ 'is-disabled': submitting }"
          @click.prevent="sendLoginCode"
        >
          {{ $t('Send a new code') }}
        </a>
        <a
          href="#"
          class="row m0 mb20 fs14 lh20 underline"
          @click.prevent="mode = MODE_CODE_REQUEST"
        >
          {{ $t('Use a different email address') }}
        </a>
      </form>
      <form
        v-else
        novalidate
        @submit.prevent="login"
      >
        <base-input
          id="email"
          v-model="email"
          class="mb10"
          type="email"
          name="email"
          autocomplete="email"
          focus
          :placeholder="$t('E-mail address')"
          :validations="[
            {
              condition: !$v.email.required && $v.email.$error,
              text: $t('Field is required.')
            },
            {
              condition: !$v.email.email && $v.email.$error,
              text: $t('Please provide valid e-mail address.')
            }
          ]"
          @blur="$v.email.$touch()"
        />
        <base-input
          id="current-password"
          v-model="password"
          class="mb10"
          type="password"
          name="current-password"
          autocomplete="current-password"
          :placeholder="$t('Password')"
          :validations="[{
            condition: !$v.password.required && $v.password.$error,
            text: $t('Field is required.')
          }]"
          @blur="$v.password.$touch()"
        />
        <div class="row mb35 align-items-center">
          <base-checkbox
            id="remember"
            v-model="remember"
            class="col-xs-7 col-sm-6 align-items-center"
          >
            {{ $t('Remember me') }}
          </base-checkbox>
          <div class="col-xs-5 col-sm-6 flex end-xs middle-xs forgot-password underline">
            <a
              href="#"
              @click.prevent="remindPassword"
            >
              {{ $t('Forgot the password?') }}
            </a>
          </div>
        </div>
        <button-full
          class="mb20 button-primary fs24"
          type="submit"
          data-testid="loginSubmit"
        >
          {{ $t('Log in to your account') }}
        </button-full>
        <a
          href="#"
          class="row m0 mb20 fs14 lh20 underline"
          data-testid="loginCodeSwitch"
          @click.prevent="mode = MODE_CODE_REQUEST"
        >
          {{ $t('Use a login code instead') }}
        </a>
      </form>
      <div class="center-xs register-link">
        {{ $t('Don\'t have an account?') }}
        <br class="hidden-desktop hidden-tablet">
        <a
          href="#"
          data-testid="registerLink"
          class="underline"
          @click.prevent="switchElem"
        >
          {{ $t('Register with Evenemangsbiljetter') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import Login from '@vue-storefront/core/compatibility/components/blocks/Auth/Login'

import ButtonFull from 'theme/components/theme/ButtonFull.vue'
import BaseCheckbox from '../Form/BaseCheckbox.vue'
import BaseInput from '../Form/BaseInput.vue'
import { required, email } from 'vuelidate/lib/validators'
import SocialLogin from 'theme/components/theme/blocks/Auth/SocialLogin'
import { LOGIN_CODE_STORE_KEY } from 'src/modules/login-code'
import { consumeRedirectNotice } from 'theme/helpers/redirectNotice'

const LOGIN_CODE_PATTERN = /^\d{6}$/

const MODE_CODE_REQUEST = 'code-request'
const MODE_CODE_ENTRY = 'code-entry'
const MODE_PASSWORD = 'password'

const LOGIN_CODE_MESSAGES = {
  LOGIN_CODE_INVALID: 'The code is not valid or has expired. Please request a new one.',
  LOGIN_CODE_UNAVAILABLE: 'Login is temporarily unavailable. Please try again shortly.',
  LOGIN_CODE_REQUEST_FAILED: 'We could not send the login code. Please try again.',
  LOGIN_CODE_RATE_LIMITED: 'Too many attempts. Please wait a few minutes and try again.',
  LOGIN_CODE_ACCOUNT_BLOCKED: 'This account cannot be signed in to right now. Please contact customer service.'
}

export default {
  components: {
    ButtonFull,
    BaseCheckbox,
    BaseInput,
    SocialLogin
  },
  mixins: [Login],
  validations: {
    email: {
      required,
      email
    },
    password: {
      required
    }
  },
  data () {
    return {
      MODE_CODE_REQUEST,
      MODE_CODE_ENTRY,
      MODE_PASSWORD,
      hasRedirect: consumeRedirectNotice(),
      mode: MODE_CODE_REQUEST,
      submitting: false,
      loginCode: '',
      codeError: '',
      isOpen: true
    }
  },
  beforeDestroy () {
    this.isOpen = false
  },
  methods: {
    close (e) {
      if (e) localStorage.removeItem('redirect')
      this.$bus.$emit('modal-hide', 'modal-signup')
    },
    login () {
      if (this.$v.$invalid) {
        this.$v.$touch()
        return
      }
      this.callLogin()
    },
    remindPassword () {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        this.notify('error', this.$t('Reset password feature does not work while offline!'))
        return
      }
      this.callForgotPassword()
    },
    async sendLoginCode () {
      this.$v.email.$touch()
      if (this.$v.email.$invalid || this.submitting) {
        return
      }

      this.codeError = ''
      this.submitting = true
      this.$bus.$emit('notification-progress-start', this.$t('Sending the login code ... '))
      try {
        await this.$store.dispatch(`${LOGIN_CODE_STORE_KEY}/requestCode`, { email: this.email })
        this.mode = MODE_CODE_ENTRY
        this.notify('success', this.$t('We have emailed you a login code.'))
      } catch (error) {
        this.notify(
          'error',
          this.$t(LOGIN_CODE_MESSAGES[error.message] || LOGIN_CODE_MESSAGES.LOGIN_CODE_REQUEST_FAILED)
        )
      } finally {
        this.stopProgressKeepingModalOverlay()
        this.submitting = false
      }
    },
    async verifyLoginCode () {
      this.codeError = ''
      if (!LOGIN_CODE_PATTERN.test(this.loginCode.trim())) {
        this.codeError = this.$t('Please enter the 6-digit code from the email.')
        return
      }

      if (this.submitting) {
        return
      }

      this.submitting = true
      this.$bus.$emit('notification-progress-start', this.$t('Logging you in ... '))
      try {
        await this.$store.dispatch(`${LOGIN_CODE_STORE_KEY}/verifyCode`, {
          email: this.email,
          code: this.loginCode.trim()
        })
        this.onSuccess()
        this.close()
        this.$bus.$emit('notification-progress-stop')
      } catch (error) {
        this.codeError = this.$t(LOGIN_CODE_MESSAGES[error.message] || LOGIN_CODE_MESSAGES.LOGIN_CODE_UNAVAILABLE)
        this.stopProgressKeepingModalOverlay()
      } finally {
        this.submitting = false
      }
    },
    stopProgressKeepingModalOverlay () {
      this.$bus.$emit('notification-progress-stop')

      if (this.isOpen) {
        this.$store.commit('ui/setOverlay', true)
      }
    },
    notify (type, message) {
      this.$store.dispatch('notification/spawnNotification', {
        type,
        message,
        action1: { label: this.$t('OK') }
      })
    },
    onSuccess () {
      this.$store.dispatch('google-tag-manager/trackEvent', {
        event: 'login'
      })
      this.$bus.$emit('user-after-login')
      this.$router.push({ path: '/my-account/orders' })
    },
    onFailure (result) {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: this.$t(result.result),
        action1: { label: this.$t('OK') }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
@import '~theme/css/variables/grid';
$color-error: color(error);
$white: color(white);
$hint-color: color(grayscale);
$link-color: color(primary);
$divider-color: #94949233;
$disabled-opacity: 0.5;
$modal-fullscreen-breakpoint: 600px;
$modal-mobile-gutter: 20px;
$modal-mobile-header-padding: 15px;
$modal-mobile-bottom-padding: 30px;

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 32px;
}

.icon-close-new {
  height: 24px;
  width: 24px;
}

.button-primary {
  height: 69px;
}

.forgot-password {
  color: #6c6c6c;

  a {
    font-size: 18px;
    color: #6c6c6c;
  }
}

.register-link {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid $divider-color;
  font-size: 16px;
  color: #6c6c6c;

  a {
    font-size: 16px;
    font-weight: 700;
    color: $link-color;
  }
}

.checkbox.checkbox-text {
  color: #6c6c6c;
}

.modal-close {
  cursor: pointer;
}

@include mobile {
  .register-link {
    line-height: 30px;
  }
}

@media (max-width: $modal-fullscreen-breakpoint) {
  .modal-header {
    padding-top: $modal-mobile-header-padding;
    padding-left: $modal-mobile-gutter;
    padding-right: $modal-mobile-gutter;
  }

  .modal-content {
    padding-left: $modal-mobile-gutter;
    padding-right: $modal-mobile-gutter;
    padding-bottom: $modal-mobile-bottom-padding;
  }

  .redirect-error {
    padding-left: $modal-mobile-gutter;
    padding-right: $modal-mobile-gutter;
  }
}

@media (max-width: 400px) {
  .modal-header {
    font-size: 20px;
  }

  .button-primary {
    font-size: 20px;
    height: 59px;
  }

  .register-link,
  .forgot-password {
    font-size: 16px;

    a {
      font-size: 16px;
    }
  }

  .modal-content {
    padding-top: 10px;
  }

  .icon-close-new {
    height: 18px;
    width: 18px;
  }
}

.redirect-error {
  color: $color-error;
}

.login-hint {
  color: $hint-color;
}

.is-disabled {
  pointer-events: none;
  opacity: $disabled-opacity;
}
</style>
